import { motion } from 'framer-motion';

const steps = [
  {
    number: '01',
    title: 'Understand the workflow',
    description: 'We start by mapping your existing processes, identifying where friction occurs, and understanding the operational context that matters.',
  },
  {
    number: '02',
    title: 'Map intake points and rules',
    description: 'Identify where information enters your system, what decisions need to be made, and what routing logic should govern the automation.',
  },
  {
    number: '03',
    title: 'Design the automation layer',
    description: 'Define the intake structure, routing rules, escalation paths, and control mechanisms that will govern the system.',
  },
  {
    number: '04',
    title: 'Build and deploy',
    description: 'Implement the solution with proper integration points, testing, and staged deployment for minimal operational disruption.',
  },
  {
    number: '05',
    title: 'Refine and expand',
    description: 'Monitor performance, refine based on real-world usage, and expand capabilities as the system proves value.',
  },
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1,
    },
  },
};

const itemVariants = {
  hidden: { opacity: 0, x: -16 },
  visible: {
    opacity: 1,
    x: 0,
    transition: { duration: 0.4 },
  },
};

export const HowWeWork = () => {
  return (
    <section
      id="process"
      data-testid="how-we-work-section"
      className="bg-white py-24 md:py-32 border-t border-slate-100"
    >
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="grid lg:grid-cols-12 gap-16">
          {/* Left Column - Header */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: '-100px' }}
            transition={{ duration: 0.5 }}
            className="lg:col-span-4"
          >
            <p className="text-xs uppercase tracking-[0.2em] text-slate-400 font-semibold mb-4">
              Engagement Process
            </p>
            <h2
              data-testid="process-heading"
              className="text-3xl md:text-4xl font-semibold tracking-tight text-slate-900 font-['Manrope'] mb-6 leading-tight"
            >
              How we work with clients
            </h2>
            <p className="text-base text-slate-600 leading-relaxed">
              A structured approach that starts with understanding and ends with operational value.
            </p>
          </motion.div>

          {/* Right Column - Steps */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true, margin: '-100px' }}
            className="lg:col-span-8"
          >
            <div className="space-y-0">
              {steps.map((step, index) => (
                <motion.div
                  key={step.number}
                  variants={itemVariants}
                  data-testid={`process-step-${step.number}`}
                  className="relative"
                >
                  <div className={`flex gap-6 md:gap-8 py-7 ${index !== steps.length - 1 ? 'border-b border-slate-100' : ''}`}>
                    {/* Step Number */}
                    <div className="flex-shrink-0 w-12">
                      <span className="text-2xl md:text-3xl font-bold text-slate-200 font-['Manrope']">
                        {step.number}
                      </span>
                    </div>

                    {/* Step Content */}
                    <div className="flex-1">
                      <h3 className="text-lg md:text-xl font-semibold text-slate-900 mb-2 font-['Manrope']">
                        {step.title}
                      </h3>
                      <p className="text-sm md:text-base text-slate-600 leading-relaxed">
                        {step.description}
                      </p>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};
